import { useEffect, useState } from 'react';
import {
  Container, Typography, TextField, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, CircularProgress, Alert, Stack,
} from '@mui/material';
import { apiFetch } from '../helpers';

type Row = { id: string; name: string; email: string; states: string[]; offers: number };

function todayLocalISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function SubadminOffers() {
  const [date, setDate] = useState<string>(todayLocalISO());
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (forDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/subadmin-stats/offers?date=${encodeURIComponent(forDate)}`);
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Failed to load');
      setRows(j.rows || []);
      setTotal(j.total || 0);
    } catch (e: any) {
      setError(e?.message || String(e));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Subadmin offers</Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          type="date"
          label="Date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 200 }}
        />
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Loading…' : `Total offers on ${date}: ${total}`}
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Subadmin</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell align="right"><strong>Offers on {date}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No subadmins found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell align="right">{r.offers}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
